import { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, MoreVertical, Edit, Trash2, CheckCircle, User, 
  ChevronLeft, ChevronRight, Calendar, Zap, UserCircle, Briefcase,
  Filter, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, subDays, isSameDay, isToday, parseISO } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { Link } from 'react-router-dom';
import LeadDetailSheet from '../components/LeadDetailSheet';
import LeadStageChangeModal from '../components/LeadStageChangeModal';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick notes presets for task completion
const TASK_COMPLETION_PRESETS = [
  "Task completed successfully",
  "Completed - Client confirmed",
  "Done - No issues",
  "Completed via phone call",
  "Completed via WhatsApp",
  "Task done - Follow up scheduled",
  "Completed - Awaiting client response"
];

const PRIORITIES = ['low', 'normal', 'high'];

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-amber-100 text-amber-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800'
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800'
};

const stageLabels = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  long_term_nurture: 'Long-Term Nurture'
};

const stageColors = {
  cold_data: 'bg-slate-100 text-slate-700',
  attempting_contact: 'bg-blue-100 text-blue-800',
  contacted: 'bg-indigo-100 text-indigo-800',
  qualified: 'bg-purple-100 text-purple-800',
  consultation_scheduled: 'bg-cyan-100 text-cyan-800',
  consultation_completed: 'bg-teal-100 text-teal-800',
  warm_follow_up: 'bg-amber-100 text-amber-800',
  rsvp_office_event: 'bg-orange-100 text-orange-800',
  attended_office_event: 'bg-lime-100 text-lime-800',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-700',
  long_term_nurture: 'bg-violet-100 text-violet-800'
};

// Task source configurations
const TASK_SOURCES = {
  from_leads: {
    label: 'From Leads',
    icon: Zap,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    activeColor: 'bg-blue-100 border-blue-400',
    iconColor: 'text-blue-600',
    description: 'Automation-generated'
  },
  self_assigned: {
    label: 'Self Assigned',
    icon: UserCircle,
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    activeColor: 'bg-emerald-100 border-emerald-400',
    iconColor: 'text-emerald-600',
    description: 'Created by you'
  },
  from_management: {
    label: 'From Management',
    icon: Briefcase,
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    activeColor: 'bg-purple-100 border-purple-400',
    iconColor: 'text-purple-600',
    description: 'Assigned by others'
  }
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState('from_leads'); // Default to From Leads
  const [ownerFilter, setOwnerFilter] = useState('all'); // For admin/owner filtering
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: '',
    priority: 'normal',
    due_date: '',
    status: 'pending',
    related_lead_id: '',
    assigned_to: ''
  });
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  
  // Custom quick notes state for task completion
  const [taskQuickNotes, setTaskQuickNotes] = useState([]);
  const [newTaskQuickNote, setNewTaskQuickNote] = useState('');
  const [showTaskQuickNoteInput, setShowTaskQuickNoteInput] = useState(false);

  // Lead detail sheet state
  const [leadSheetOpen, setLeadSheetOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Lead stage change state (for Edit Task modal)
  const [stageConfig, setStageConfig] = useState(null);
  const [stageChangeModal, setStageChangeModal] = useState({
    open: false, leadId: null, currentStage: null, newStage: null,
  });

  const openLeadSheet = (leadId) => {
    setSelectedLeadId(leadId);
    setLeadSheetOpen(true);
  };

  const { hasPermission, role, user } = usePermissions();
  const canCreateSelf = hasPermission('tasks_create_self');
  const canCreateOthers = hasPermission('tasks_create_others');
  const canUpdate = hasPermission('tasks_update_assigned');
  const canDeleteOwn = hasPermission('tasks_delete_own');
  const canDeleteAny = hasPermission('tasks_delete_any');
  const isReadOnly = role === 'top_management' || role === 'finance';
  
  // Owner/Admin can see all tasks
  const canViewAll = ['owner', 'admin', 'sales_manager'].includes(role);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      logger.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      logger.error('Error fetching leads:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.filter(u => u.status === 'approved' || u.status === 'active'));
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  }, []);

  // Fetch user's custom quick notes for task completion
  const fetchTaskQuickNotes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/quick-notes?category=task_completion`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTaskQuickNotes(data);
      }
    } catch (error) {
      logger.error('Error fetching task quick notes:', error);
    }
  }, []);

  // Fetch stage settings (used to drive the "Change Lead Stage" dropdown)
  const fetchStageConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/stage-settings`, { credentials: 'include' });
      if (r.ok) setStageConfig(await r.json());
    } catch (err) {
      logger.error('Failed to load stage settings', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchLeads();
    fetchTaskQuickNotes();
    fetchStageConfig();
    if (canCreateOthers || canViewAll) {
      fetchUsers();
    }
  }, [canCreateOthers, canViewAll, fetchTasks, fetchLeads, fetchTaskQuickNotes, fetchStageConfig, fetchUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a new custom quick note for task completion
  const handleCreateTaskQuickNote = async () => {
    if (!newTaskQuickNote.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/quick-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note_text: newTaskQuickNote.trim(),
          category: 'task_completion'
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Quick note created');
        setNewTaskQuickNote('');
        setShowTaskQuickNoteInput(false);
        fetchTaskQuickNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create quick note');
      }
    } catch (error) {
      logger.error('Error creating quick note:', error);
      toast.error('Failed to create quick note');
    }
  };

  // Delete a custom quick note
  const handleDeleteTaskQuickNote = async (noteId) => {
    try {
      const response = await fetch(`${API_URL}/api/quick-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Quick note deleted');
        fetchTaskQuickNotes();
      } else {
        toast.error('Failed to delete quick note');
      }
    } catch (error) {
      logger.error('Error deleting quick note:', error);
      toast.error('Failed to delete quick note');
    }
  };

  // Determine task source for backward compatibility
  const getTaskSource = useCallback((task) => {
    if (task.task_source) return task.task_source;
    if (task.created_by === 'automation') return 'from_leads';
    if (task.created_by === task.assigned_to) return 'self_assigned';
    return 'from_management';
  }, []);

  // Get tasks filtered by date, category, and owner
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Filter by date
      if (!task.due_date) return false;
      const taskDate = typeof task.due_date === 'string' ? parseISO(task.due_date) : task.due_date;
      if (!isSameDay(taskDate, selectedDate)) return false;
      
      // Filter by category
      if (getTaskSource(task) !== selectedCategory) return false;
      
      // Filter by owner (for admin/owner)
      if (canViewAll && ownerFilter !== 'all') {
        if (task.assigned_to !== ownerFilter) return false;
      }
      
      // Hide completed tasks by default
      if (task.status === 'completed') return false;
      
      return true;
    });
  }, [tasks, selectedDate, selectedCategory, ownerFilter, canViewAll, getTaskSource]);

  // Get count per category for selected date
  const getCategoryCount = (sourceKey) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = typeof task.due_date === 'string' ? parseISO(task.due_date) : task.due_date;
      if (!isSameDay(taskDate, selectedDate)) return false;
      if (getTaskSource(task) !== sourceKey) return false;
      if (task.status === 'completed') return false;
      if (canViewAll && ownerFilter !== 'all' && task.assigned_to !== ownerFilter) return false;
      return true;
    }).length;
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const foundUser = users.find(u => u.user_id === userId);
    return foundUser ? foundUser.name : 'Unknown';
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // Convert local datetime-local string to UTC ISO so the reminder fires at the
      // exact wall-clock time the user picked regardless of their timezone.
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : formData.due_date,
        assigned_to: formData.assigned_to || undefined
      };
      
      const response = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Task created successfully');
        fetchTasks();
        setIsCreateOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      toast.error('Error creating task');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : formData.due_date,
      };
      const response = await fetch(`${API_URL}/api/tasks/${selectedTask.task_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Task updated successfully');
        fetchTasks();
        setIsEditOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update task');
      }
    } catch (error) {
      toast.error('Error updating task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Task deleted successfully');
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete task');
      }
    } catch (error) {
      toast.error('Error deleting task');
    }
  };

  const handleCompleteTask = async () => {
    if (!taskToComplete || !completionNotes.trim()) {
      toast.error('Please enter completion notes');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskToComplete.task_id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completion_notes: completionNotes })
      });

      if (response.ok) {
        toast.success('Task completed successfully');
        const completedTask = taskToComplete;
        fetchTasks();
        setCompleteDialogOpen(false);
        setTaskToComplete(null);
        setCompletionNotes('');

        // Post-complete optional stage update — only if task tied to a lead and
        // the lead has at least one valid stage transition. Skipping the modal
        // never undoes the completion.
        if (completedTask?.related_lead_id && stageConfig) {
          try {
            const lr = await fetch(`${API_URL}/api/leads/${completedTask.related_lead_id}`, { credentials: 'include' });
            if (lr.ok) {
              const lead = await lr.json();
              const allowed = (stageConfig.transitions || {})[lead.stage] || [];
              if (allowed.length > 0) {
                setStageChangeModal({
                  open: true,
                  leadId: completedTask.related_lead_id,
                  currentStage: lead.stage,
                  newStage: null, // inline picker
                });
              }
            }
          } catch (err) {
            logger.error('Post-complete stage fetch error', err);
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to complete task');
      }
    } catch (error) {
      toast.error('Error completing task');
    }
  };

  const openEditDialog = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      task_type: task.task_type || '',
      priority: task.priority,
      due_date: task.due_date ? format(new Date(task.due_date), "yyyy-MM-dd'T'HH:mm") : '',
      status: task.status,
      related_lead_id: task.related_lead_id || '',
      assigned_to: task.assigned_to || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: '',
      priority: 'normal',
      due_date: format(selectedDate, "yyyy-MM-dd'T'HH:mm"),
      status: 'pending',
      related_lead_id: '',
      assigned_to: ''
    });
    setSelectedTask(null);
  };

  const navigateDate = (direction) => {
    setSelectedDate(prev => direction === 'next' ? addDays(prev, 1) : subDays(prev, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tasks-page">
      {/* Header with Date Navigation */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tasks</h1>
          <p className="text-sm text-slate-500">
            {isToday(selectedDate) ? "Today's tasks" : format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Date Navigation */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1 shadow-sm">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 rounded-lg"
              onClick={() => navigateDate('prev')}
              data-testid="prev-day-btn"
            >
              <ChevronLeft size={18} />
            </Button>
            
            <div className="px-4 text-center min-w-[180px]">
              <p className="text-sm font-semibold text-slate-900">
                {isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEE, MMM d')}
              </p>
              <p className="text-xs text-slate-500">{format(selectedDate, 'yyyy')}</p>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 rounded-lg"
              onClick={() => navigateDate('next')}
              data-testid="next-day-btn"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
          
          {!isToday(selectedDate) && (
            <Button variant="outline" size="sm" onClick={goToToday} className="rounded-lg">
              <Calendar size={14} className="mr-2" />
              Today
            </Button>
          )}
          
          {/* Owner Filter (for Admin/Owner) */}
          {canViewAll && users.length > 0 && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[180px]" data-testid="owner-filter">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder="Filter by owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Create Task Button */}
          {canCreateSelf && !isReadOnly && (
            <Button 
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }} 
              className="btn-accent rounded-lg" 
              data-testid="create-task-btn"
            >
              <Plus size={18} className="mr-2" /> New Task
            </Button>
          )}
        </div>
      </div>

      {/* Category Selector Cards */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(TASK_SOURCES).map(([key, config]) => {
          const Icon = config.icon;
          const count = getCategoryCount(key);
          const isActive = selectedCategory === key;
          
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                isActive 
                  ? config.activeColor 
                  : `${config.bgColor} ${config.borderColor} hover:border-slate-300`
              }`}
              data-testid={`category-${key}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-white/80 shadow-sm`}>
                    <Icon className={`h-5 w-5 ${config.iconColor}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{config.label}</p>
                    <p className="text-xs text-slate-500">{config.description}</p>
                  </div>
                </div>
                <Badge variant={isActive ? "default" : "secondary"} className="text-lg px-3">
                  {count}
                </Badge>
              </div>
            </button>
          );
        })}
      </div>

      {/* Tasks Table */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[300px]">Task</TableHead>
                <TableHead>Lead</TableHead>
                {canViewAll && <TableHead>Assigned To</TableHead>}
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canViewAll ? 6 : 5} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Calendar className="h-10 w-10 text-slate-300" />
                      <p className="text-slate-500">No tasks for this day</p>
                      <p className="text-xs text-slate-400">
                        {selectedCategory === 'from_leads' && 'Automation-generated tasks will appear here'}
                        {selectedCategory === 'self_assigned' && 'Create a task for yourself'}
                        {selectedCategory === 'from_management' && 'Tasks assigned by management will appear here'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow 
                    key={task.task_id} 
                    className="hover:bg-slate-50"
                    data-testid={`task-row-${task.task_id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-slate-500 truncate max-w-[280px]">{task.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {task.lead_name ? (
                        <div>
                          <button 
                            onClick={() => task.related_lead_id && openLeadSheet(task.related_lead_id)}
                            className="text-sm text-blue-600 hover:underline flex items-center gap-1 text-left"
                            data-testid={`lead-link-${task.task_id}`}
                          >
                            <User size={12} />
                            {task.lead_name}
                          </button>
                          {task.lead_stage && (
                            <Badge className={`mt-1 text-[10px] ${stageColors[task.lead_stage] || 'bg-slate-100'}`}>
                              {stageLabels[task.lead_stage] || task.lead_stage}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    {canViewAll && (
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {getUserName(task.assigned_to)}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge className={`${priorityColors[task.priority] || priorityColors.normal}`}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[task.status] || statusColors.pending}`}>
                        {task.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`task-menu-${task.task_id}`}>
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {task.related_lead_id && task.lead_name && (
                            <DropdownMenuItem onClick={() => openLeadSheet(task.related_lead_id)} data-testid={`view-lead-menu-${task.task_id}`}>
                              <User size={14} className="mr-2 text-blue-600" /> View Lead
                            </DropdownMenuItem>
                          )}
                          {task.status !== 'completed' && canUpdate && (
                            <DropdownMenuItem onClick={() => {
                              setTaskToComplete(task);
                              setCompleteDialogOpen(true);
                            }}>
                              <CheckCircle size={14} className="mr-2 text-green-600" /> Complete
                            </DropdownMenuItem>
                          )}
                          {canUpdate && !isReadOnly && (
                            <DropdownMenuItem onClick={() => openEditDialog(task)} data-testid={`edit-task-menu-${task.task_id}`}>
                              <Edit size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {(canDeleteAny || (canDeleteOwn && task.created_by === user?.user_id)) && (
                            <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)} className="text-red-600">
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="task-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="due_date">Due Date &amp; Time *</Label>
                <Input
                  id="due_date"
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  data-testid="task-create-due-date"
                />
                <p className="text-[10px] text-slate-400 mt-1">Weekends auto-shift to Monday · Reminder fires at this exact time</p>
              </div>
              
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="related_lead">Related Lead (optional)</Label>
              <Select 
                value={formData.related_lead_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, related_lead_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {leads.map(lead => (
                    <SelectItem key={lead.lead_id} value={lead.lead_id}>
                      {lead.first_name} {lead.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {canCreateOthers && (
              <div>
                <Label htmlFor="assigned_to">Assign To</Label>
                <Select 
                  value={formData.assigned_to || 'myself'} 
                  onValueChange={(v) => setFormData({ ...formData, assigned_to: v === 'myself' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign to yourself" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="myself">Myself</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-accent">Create Task</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateTask} className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-due_date">Due Date &amp; Time</Label>
                <Input
                  id="edit-due_date"
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="task-edit-due-date"
                />
                <p className="text-[10px] text-slate-400 mt-1">Reminder fires at this exact time</p>
              </div>
              
              <div>
                <Label htmlFor="edit-priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Change Lead Stage — only when task is tied to a lead and stage config is loaded */}
            {selectedTask?.related_lead_id && stageConfig && (() => {
              const stages = stageConfig.stages || [];
              const transitions = stageConfig.transitions || {};
              const currentStage = selectedTask.lead_stage || null;
              const labels = Object.fromEntries(stages.map(s => [s.stage_id, s.label]));
              const allowedIds = currentStage && transitions[currentStage] ? transitions[currentStage] : [];
              const currentLabel = labels[currentStage] || currentStage || 'Unknown';
              return (
                <div className="border-t pt-4" data-testid="task-edit-change-lead-stage-section">
                  <Label className="text-sm font-medium">Change Lead Stage</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                    Save time — update this lead's stage directly from here. A confirmation popup will ask for contact mode + notes, just like the Leads page.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">Current: {currentLabel}</Badge>
                    <span className="text-slate-400 text-sm">→</span>
                    <Select
                      value=""
                      onValueChange={(newStageId) => {
                        if (!newStageId) return;
                        setStageChangeModal({
                          open: true,
                          leadId: selectedTask.related_lead_id,
                          currentStage,
                          newStage: newStageId,
                        });
                      }}
                    >
                      <SelectTrigger className="h-9 w-[220px]" data-testid="task-change-lead-stage-trigger">
                        <SelectValue placeholder={allowedIds.length ? 'Select new stage…' : 'No transitions available'} />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedIds.length === 0 ? (
                          <div className="px-2 py-1.5 text-xs text-slate-400">No valid transitions from this stage</div>
                        ) : (
                          allowedIds.map(sid => (
                            <SelectItem
                              key={sid}
                              value={sid}
                              data-testid={`task-change-lead-stage-opt-${sid}`}
                            >
                              {labels[sid] || sid}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })()}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-accent">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Task Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Complete Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {taskToComplete && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{taskToComplete.title}</p>
                {taskToComplete.lead_name && (
                  <p className="text-sm text-slate-500">Lead: {taskToComplete.lead_name}</p>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="completion-notes">Completion Notes *</Label>
              <Textarea
                id="completion-notes"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Describe what was done..."
                className="min-h-[100px]"
                required
              />
            </div>
            
            {/* Preset Quick Notes */}
            <div>
              <p className="text-xs text-slate-500 mb-2">Preset notes:</p>
              <div className="flex flex-wrap gap-1.5">
                {TASK_COMPLETION_PRESETS.map((note) => (
                  <Button
                    key={note}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 px-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                    onClick={() => setCompletionNotes(prev => prev ? `${prev}\n${note}` : note)}
                  >
                    {note.length > 25 ? note.substring(0, 25) + '...' : note}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Custom Quick Notes */}
            {taskQuickNotes.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Your custom notes:</p>
                <div className="flex flex-wrap gap-1.5">
                  {taskQuickNotes.map((qn) => (
                    <div key={qn.note_id} className="flex items-center gap-0.5 group">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 px-2 hover:bg-green-50 hover:text-green-700 hover:border-green-200 rounded-r-none"
                        onClick={() => setCompletionNotes(prev => prev ? `${prev}\n${qn.note_text}` : qn.note_text)}
                      >
                        {qn.note_text.length > 25 ? qn.note_text.substring(0, 25) + '...' : qn.note_text}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 px-1 text-red-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 rounded-l-none opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteTaskQuickNote(qn.note_id)}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add Custom Quick Note */}
            <div className="pt-2 border-t">
              {showTaskQuickNoteInput ? (
                <div className="flex gap-2">
                  <Input
                    value={newTaskQuickNote}
                    onChange={(e) => setNewTaskQuickNote(e.target.value)}
                    placeholder="Type your custom quick note..."
                    className="text-xs h-8"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTaskQuickNote()}
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 px-3 bg-green-600 hover:bg-green-700"
                    onClick={handleCreateTaskQuickNote}
                    disabled={!newTaskQuickNote.trim()}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => { setShowTaskQuickNoteInput(false); setNewTaskQuickNote(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-green-600 hover:text-green-700 hover:bg-green-50 h-7"
                  onClick={() => setShowTaskQuickNoteInput(true)}
                >
                  <PlusCircle size={12} className="mr-1" /> Add your own quick note
                </Button>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => {
                setCompleteDialogOpen(false);
                setTaskToComplete(null);
                setCompletionNotes('');
                setShowTaskQuickNoteInput(false);
                setNewTaskQuickNote('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleCompleteTask} className="bg-green-600 hover:bg-green-700 text-white">
                Mark Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Sheet */}
      <LeadDetailSheet
        open={leadSheetOpen}
        onOpenChange={setLeadSheetOpen}
        leadId={selectedLeadId}
      />

      {/* Lead Stage Change (invoked from Edit Task modal AND post-Complete) */}
      <LeadStageChangeModal
        open={stageChangeModal.open}
        onOpenChange={(v) => setStageChangeModal(prev => ({ ...prev, open: v }))}
        leadId={stageChangeModal.leadId}
        currentStage={stageChangeModal.currentStage}
        newStage={stageChangeModal.newStage}
        stageConfig={stageConfig}
        title={stageChangeModal.newStage ? 'Update Lead Stage' : 'Update lead stage? (optional)'}
        skipLabel={stageChangeModal.newStage ? 'Cancel' : 'Skip'}
        onSuccess={() => {
          // Refresh tasks so lead_stage badge reflects the new stage
          fetchTasks();
          // Also update selectedTask so the open Edit modal shows the new current stage
          setSelectedTask(prev => prev ? { ...prev, lead_stage: stageChangeModal.newStage } : prev);
          setStageChangeModal({ open: false, leadId: null, currentStage: null, newStage: null });
        }}
      />
    </div>
  );
}
